import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  Text,
  Table,
  Box,
  TextField,
  SearchableDropdown,
  SearchableDropdownOption,
  CreateNewAction,
  OptionWithEditIcon,
  Flex,
  IconButton,
} from 'components/ds'

import styles from '../styles.module.css'
import { RIGHTS_DROPDOWN_WIDTH } from 'features/rights-data/constants'
import { filterRightsHolderOptions, findErrorsForField } from './utils'
import {
  RightsHolderEntity,
  SplitValidationError,
} from '@disco/types/types/rights-data'
import { formatIdentifiersForDisplay } from 'features/rights-data/utils/identifier-validation'
import { useNavigate } from 'react-router-dom'
import {
  RecordingSplitData,
  SplitsKey,
  hasSplitFieldValue,
} from '@disco/state-management/reducers/rightsManagement'
import { RxCross2 } from '@react-icons/all-files/rx/RxCross2'

interface RecordingRowProps {
  split: RecordingSplitData
  splitsKey: SplitsKey
  onUpdate: (
    splitId: number | string,
    changes: Partial<Omit<RecordingSplitData, 'id'>>,
  ) => void
  onDelete: (splitId: number | string) => void
  onValidate?: (
    splitId: number | string,
    field: string,
    message: string,
  ) => void
  onClearValidation?: (splitId: number | string) => void
  validationError?: SplitValidationError
  labels: RightsHolderEntity[]
  isRightsholdersLoading: boolean
  /** Disables all form inputs (used while a save is in flight) */
  disabled?: boolean
}

export function RecordingRow({
  split,
  splitsKey,
  onUpdate,
  onDelete,
  onValidate,
  onClearValidation,
  validationError,
  labels,
  isRightsholdersLoading,
  disabled = false,
}: RecordingRowProps) {
  const navigate = useNavigate()
  const [splitInput, setSplitInput] = useState<string>(
    split.split ? String(split.split) : '',
  )
  const [rightsholderSearch, setRightsholderSearch] = useState('')
  const [labelSearch, setLabelSearch] = useState('')

  const hasRightsholder = hasSplitFieldValue(split.rightsholderId)
  const hasLabel = hasSplitFieldValue(split.labelId)
  const hasSplit = hasSplitFieldValue(split.split)
  const hasAnyFieldFilled = hasRightsholder || hasLabel || hasSplit
  const isEmptyPlaceholder = !hasAnyFieldFilled

  useEffect(() => {
    if (!onValidate || !onClearValidation) return

    if (isEmptyPlaceholder) {
      onClearValidation(split.id)
    } else {
      if (!hasRightsholder && !hasLabel && hasSplit) {
        onValidate(split.id, 'label', 'Please add a rightsholder or label')
        onValidate(
          split.id,
          'rightsholder',
          'Please add a rightsholder or label',
        )
      } else {
        onValidate(split.id, 'label', '')
        onValidate(split.id, 'rightsholder', '')
      }

      // Only validate split > 100% in real-time
      if ((split.split ?? 0) > 100) {
        onValidate(split.id, 'split_percentage', 'Cannot exceed 100%')
      } else {
        onValidate(split.id, 'split_percentage', '')
      }
    }
  }, [
    split.id,
    hasRightsholder,
    hasLabel,
    split.split,
    isEmptyPlaceholder,
    onValidate,
    onClearValidation,
  ])

  const errorFieldTypes = ['label', 'rightsholder', 'split_percentage']
  const [labelError, rightsholderError, splitError] = findErrorsForField(
    validationError,
    errorFieldTypes,
  )

  const handleSplitChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      // Allow empty string, digits, and up to 2 decimal places
      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
        setSplitInput(value)
        const numValue = parseFloat(value) || 0
        onUpdate(split.id, { split: numValue })
      }
    },
    [split.id, onUpdate],
  )

  const handleSplitBlur = useCallback(() => {
    if (splitInput === '') return
    const numValue = parseFloat(splitInput)
    if (isNaN(numValue)) {
      setSplitInput('')
      return
    }
    const roundedValue = Math.round(numValue * 100) / 100
    setSplitInput(String(roundedValue))
  }, [splitInput])

  const handleRightsholderChange = useCallback(
    (option: SearchableDropdownOption | null) => {
      onUpdate(split.id, {
        rightsholderId: option ? parseInt(option.id, 10) : null,
      })
    },
    [split.id, onUpdate],
  )

  const handleLabelChange = useCallback(
    (option: SearchableDropdownOption | null) => {
      onUpdate(split.id, {
        labelId: option ? parseInt(option.id, 10) : null,
      })
    },
    [split.id, onUpdate],
  )

  const rightsholderActionComponent = useCallback(
    (searchValue: string) => (
      <CreateNewAction
        label="Create new rightsholder"
        to="rights-data/rightsholders/new"
        searchText={searchValue}
        splitId={split.id}
        splitsKey={splitsKey}
      />
    ),
    [split.id, splitsKey],
  )

  const labelActionComponent = useCallback(
    (searchValue: string) => (
      <CreateNewAction
        label="Create new label"
        to="rights-data/labels/new"
        searchText={searchValue}
        splitId={split.id}
        splitsKey={splitsKey}
      />
    ),
    [split.id, splitsKey],
  )

  const renderRightsholderOption = useCallback(
    (option: SearchableDropdownOption<RightsHolderEntity>) => {
      try {
        const secondaryText = formatIdentifiersForDisplay(
          option.data?.external_identifiers,
        )
        return (
          <OptionWithEditIcon
            label={option.label}
            secondaryText={secondaryText}
            onEditClick={() => {
              navigate(`rights-data/rightsholders/${option.id}/edit`, {
                state: { splitId: split.id, splitsKey },
              })
            }}
          />
        )
      } catch (e) {
        console.log(e)
      }
    },
    [navigate, split.id, splitsKey],
  )

  const renderLabelOption = useCallback(
    (option: SearchableDropdownOption<RightsHolderEntity>) => {
      const secondaryText = formatIdentifiersForDisplay(
        option.data?.external_identifiers,
      )
      return (
        <OptionWithEditIcon
          label={option.label}
          secondaryText={secondaryText}
          onEditClick={() => {
            navigate(`rights-data/labels/${option.id}/edit`, {
              state: { splitId: split.id, splitsKey },
            })
          }}
        />
      )
    },
    [navigate, split.id, splitsKey],
  )

  const labelOptions = useMemo(
    () => filterRightsHolderOptions(labels, labelSearch),
    [labels, labelSearch],
  )

  const rightsholderOptions = useMemo(
    () => filterRightsHolderOptions(labels, rightsholderSearch),
    [labels, rightsholderSearch],
  )

  const labelValue =
    typeof split.labelId === 'number' ? String(split.labelId) : undefined
  const rightsholderValue =
    typeof split.rightsholderId === 'number'
      ? String(split.rightsholderId)
      : undefined

  const handleDelete = useCallback(() => {
    onDelete(split.id)
  }, [split.id, onDelete])

  return (
    <Table.Row>
      <Table.Cell>
        <Box className={rightsholderError ? styles.fieldWithError : undefined}>
          <SearchableDropdown
            value={rightsholderValue}
            onSelect={handleRightsholderChange}
            onSearchChange={setRightsholderSearch}
            options={rightsholderOptions}
            loading={isRightsholdersLoading}
            placeholder="Rightsholder"
            renderOption={renderRightsholderOption}
            aria-label="Rightsholder dropdown"
            maxHeight={208}
            customDropdownWidth={RIGHTS_DROPDOWN_WIDTH}
            actionComponent={rightsholderActionComponent}
            disabled={disabled}
          />
          {rightsholderError && (
            <Text size="1" className={styles.errorMessage}>
              {rightsholderError}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell>
        <Box className={labelError ? styles.fieldWithError : undefined}>
          <SearchableDropdown
            value={labelValue}
            onSelect={handleLabelChange}
            onSearchChange={setLabelSearch}
            options={labelOptions}
            loading={isRightsholdersLoading}
            placeholder="Label"
            renderOption={renderLabelOption}
            aria-label="Label dropdown"
            maxHeight={208}
            customDropdownWidth={RIGHTS_DROPDOWN_WIDTH}
            actionComponent={labelActionComponent}
            disabled={disabled}
          />
          {labelError && (
            <Text size="1" className={styles.errorMessage}>
              {labelError}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell>
        <Box className={splitError ? styles.fieldWithError : undefined}>
          <TextField.Root
            value={splitInput}
            onChange={handleSplitChange}
            onBlur={handleSplitBlur}
            placeholder="Split"
            color={splitError ? 'red' : undefined}
            disabled={disabled}
          />
        </Box>
      </Table.Cell>
      <Table.Cell justify="center" width="24px" maxWidth="24px">
        <Flex justify="center" align="start" height="100%" mt="2">
          <IconButton
            variant="ghost"
            color="gray"
            size="1"
            onClick={handleDelete}
            aria-label="Remove writer"
            disabled={disabled}
          >
            <RxCross2 />
          </IconButton>
        </Flex>
      </Table.Cell>
    </Table.Row>
  )
}
